import { useState } from 'react';
import { decidirDivergenciaDeSaldo } from '../../api/divergencias-saldo.js';
import type {
  DecisaoDaDivergenciaDeSaldo,
  DivergenciaDeSaldo,
} from '../../api/divergencias-saldo.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoAreaTexto } from '../../componentes/Campo.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { LIMITE_DO_TEXTO } from '../movimentacoes/DialogoDeDecisao.js';
import { formatarQuantidade } from '../movimentacoes/quantidade.js';
import { linhasDaComparacao } from './divergencia-de-saldo.js';
import '../movimentacoes/DialogosDaAbertura.css';
import './DialogoDeDecisaoDeSaldo.css';

/**
 * O rótulo do botão diz o número que vai valer: "Manter 10", "Atualizar para
 * 7". Mesmo desenho da divergência da movimentação em duas etapas.
 */
export function rotuloDaDecisaoDeSaldo(
  divergencia: DivergenciaDeSaldo,
  decisao: DecisaoDaDivergenciaDeSaldo,
): string {
  return decisao === 'Manter'
    ? `Manter ${formatarQuantidade(divergencia.quantidadeDisponivelLocal)}`
    : `Atualizar para ${formatarQuantidade(divergencia.quantidadeDisponivelErp)}`;
}

type Props = {
  readonly divergencia: DivergenciaDeSaldo;
  readonly decisao: DecisaoDaDivergenciaDeSaldo;
  readonly aoDecidir: (decidida: DivergenciaDeSaldo) => void;
  readonly aoCancelar: () => void;
};

/**
 * Decisão da divergência de saldo (r1-sync).
 *
 * O diálogo escreve a consequência em número antes do clique e mostra, lado a
 * lado, cada quantidade que a sincronização comparou: `Atualizar` aplica as do
 * ERP; `Manter` descarta a leitura e o saldo fica como está. A decisão vai para
 * a auditoria e não se desfaz — a segunda tentativa volta
 * `DIVERGENCIA_SALDO_JA_DECIDIDA`, e a recusa aparece aqui dentro, sem fechar.
 */
export function DialogoDeDecisaoDeSaldo({ divergencia, decisao, aoDecidir, aoCancelar }: Props) {
  const [justificativa, definirJustificativa] = useState('');
  const [agindo, definirAgindo] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const local = rotuloDoLocal(divergencia.codFil, divergencia.codDep);
  const aqui = formatarQuantidade(divergencia.quantidadeDisponivelLocal);
  const doErp = formatarQuantidade(divergencia.quantidadeDisponivelErp);
  const rotulo = rotuloDaDecisaoDeSaldo(divergencia, decisao);

  async function confirmar() {
    definirAgindo(true);
    definirErro(null);

    try {
      aoDecidir(await decidirDivergenciaDeSaldo(divergencia.id, { decisao, justificativa }));
    } catch (falha: unknown) {
      definirErro(mensagemDoErro(falha));
      definirAgindo(false);
    }
  }

  return (
    <DialogoConfirmacao
      titulo={
        decisao === 'Manter'
          ? `Manter o saldo de ${divergencia.codPro} em ${local}?`
          : `Atualizar o saldo de ${divergencia.codPro} em ${local} com o do ERP?`
      }
      rotuloConfirmar={rotulo}
      rotuloCancelar="Voltar"
      variante="primario"
      agindo={agindo}
      aoConfirmar={() => {
        void confirmar();
      }}
      aoCancelar={aoCancelar}
    >
      {decisao === 'Manter' ? (
        <p>
          O saldo disponível continua com {aqui}; a leitura do ERP ({doErp}) é descartada.
        </p>
      ) : (
        <p>
          O saldo disponível passa de {aqui} para {doErp}, como está no ERP, junto com as demais
          quantidades lidas abaixo.
        </p>
      )}

      <table className="decisao-saldo__comparacao">
        <caption className="apenas-leitor-de-tela">Quantidades comparadas</caption>
        <thead>
          <tr>
            <th scope="col">Quantidade</th>
            <th scope="col">No painel</th>
            <th scope="col">No ERP</th>
          </tr>
        </thead>
        <tbody>
          {linhasDaComparacao(divergencia).map((linha) => (
            <tr
              key={linha.chave}
              className={linha.difere ? 'decisao-saldo__linha--difere' : undefined}
            >
              <th scope="row">{linha.rotulo}</th>
              <td className="mono">{linha.local}</td>
              <td className="mono">{linha.erp}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p>A decisão fica na auditoria com o seu login e não se desfaz.</p>
      <CampoAreaTexto
        rotulo="Justificativa (opcional)"
        valor={justificativa}
        aoMudar={definirJustificativa}
        maximoDeCaracteres={LIMITE_DO_TEXTO}
        desabilitado={agindo}
      />
      {erro !== null && <p className="dialogo-abertura__erro">{erro}</p>}
    </DialogoConfirmacao>
  );
}
